"use client";

import { useWeb3 } from "logic/components";
import { useMemo } from "react";
import {
  Abi,
  ContractFunctionArgs,
  ContractFunctionName,
  encodeFunctionData,
  encodeAbiParameters,
  parseAbiParameters,
  Address,
} from "viem";

import { useContractStaticCall } from "./useContractStaticCall";

const KERNEL_EXECUTE_ABI = [
  {
    name: "execute",
    type: "function",
    stateMutability: "payable",
    inputs: [
      { name: "execMode", type: "bytes32" },
      { name: "executionCalldata", type: "bytes" },
    ],
    outputs: [],
  },
] as const;

// CALLTYPE_BATCH (0x01) + EXECTYPE_DEFAULT (0x00) + 30 zero bytes
const BATCH_EXEC_MODE = "0x0100000000000000000000000000000000000000000000000000000000000000" as `0x${string}`;

export interface BatchCall<
  abi extends Abi | readonly unknown[] = Abi,
  functionName extends ContractFunctionName<abi, "nonpayable" | "payable"> = ContractFunctionName<
    abi,
    "nonpayable" | "payable"
  >,
> {
  address: Address;
  chainId: number;
  abi: abi;
  functionName: functionName;
  args?: ContractFunctionArgs<abi, "nonpayable" | "payable", functionName>;
  value?: bigint;
}

export function useBatchStaticCall(
  calls: BatchCall[],
  options?: { enabled?: boolean; chainId?: number; watch?: boolean },
) {
  const { address } = useWeb3();

  const executionCalldata = useMemo(() => {
    if (!calls.length) return undefined;

    try {
      const executions = calls.map((call) => ({
        target: call.address,
        value: call.value ?? 0n,
        callData: encodeFunctionData({
          abi: call.abi,
          functionName: call.functionName,
          args: call.args ?? [],
        }),
      }));

      return encodeAbiParameters(parseAbiParameters("(address target, uint256 value, bytes callData)[]"), [executions]);
    } catch {
      return undefined;
    }
  }, [calls]);

  const result = useContractStaticCall({
    address: address,
    abi: [
      ...KERNEL_EXECUTE_ABI,
      ...calls
        .map((call) => call.abi)
        .flat()
        .filter((item) => item.type === "error"),
    ],
    functionName: "execute",
    args: [BATCH_EXEC_MODE, executionCalldata!],
    chainId: options?.chainId,
    watch: options?.watch,
    query: {
      enabled: (options?.enabled ?? true) && !!address && !!executionCalldata,
    },
  });

  return {
    ...result,
    error: (options?.enabled ?? true) ? result.error : null,
    data: result.error ? undefined : result.data,
  };
}
